import React, {useContext} from 'react'
import {Box, Button, FormControl, FormHelperText, FormLabel, Grid, Stack, TextField, Typography,} from '@mui/material'
import {AppContext} from '../../contexts/AppContextProvider'
import {CancelButton, PreviousButton} from '../StepperButtons'
import {useForm} from '../../hooks/useForm'
import {InstrumentInfoState} from '../../contexts/types'
import {OrgCustomizationContext} from "../../themes/OrgCustomizationProvider";
import {setInstrumentInfo} from "../../contexts/InstrumentInfoReducer";
import UseTheHackiestPageNavigation from "../../hooks/useTheHackiestPageNavigation";

const InstrumentInformation: React.FC = () => {
    const {navigateForward} = UseTheHackiestPageNavigation()
    const {complaint, dispatch, postNotification, clearNotification} = useContext(AppContext)
    const {t} = useContext(OrgCustomizationContext)

    const {
        data,
        errors,
        handleChange,
        validateFields,
        enableOnChangeValidation,
    } = useForm<InstrumentInfoState>({
        initialValues: {
            ...complaint.instrumentInfo,
        },
        validations: {
            numberOfTimesUsed: {
                required: {
                    value: (value, data) => {
                        if (data.numberOfTimesUsed || data.ageOfDevice) {
                            return true
                        }
                        return false
                    },
                    message: t('ii_at_least_one_field_is_required'),
                },
            },
        },
    })

    const navigateToNextScreen = () => {
        navigateForward()
    }

    const handleTimesChange = (value: string) => {
        const re = /^[0-9]*$/
        const match = re.test(value)
        if (match) {
            handleChange('numberOfTimesUsed', value)
        }
    }

    const handleAgeChange = (value: string) => {
        const re = /^(\d{0,3})$/
        const match = re.test(value)
        if (match) {
            handleChange('ageOfDevice', value)
        }
    }

    const handleSubmit = () => {
        // Activate field level validation tracking
        enableOnChangeValidation(true)

        // Validate the form fields
        const isFormValid = validateFields(data)

        // Stop processing if the form has invalid fields
        if (!isFormValid) {
            postNotification('error', t('error_mess'))
            return
        }

        // Form is valid, proceed with processing

        // Close the alert message in case it is open
        clearNotification()

        // Package up the form data and update app state
        const instrumentInfo: InstrumentInfoState = {
            ...data,
        }

        dispatch(setInstrumentInfo(instrumentInfo))

        navigateToNextScreen()
    }

    return (
        <Grid container>
            <Stack spacing={2}>
                <Typography variant='h5' sx={{mb: 3}}>
                    {t('ii_instrument_information')}
                </Typography>

                <FormControl sx={{mb: 2}}>
                    <FormLabel sx={{mb: 1}}>
                        {t('ii_how_many_times')}
                    </FormLabel>
                </FormControl>
                <Stack
                    direction={{xs: 'column', sm: 'row'}}
                    spacing={{xs: 0, md: 3}}
                >
                    <FormControl sx={{mb: 2}}>
                        <FormLabel sx={{mb: 1}}>
                            {t('ii_number_of_times')}:
                        </FormLabel>
                        <TextField
                            sx={{width: '11rem'}}
                            size='small'
                            error={!!errors.numberOfTimesUsed}
                            value={data.numberOfTimesUsed || ''}
                            onChange={(e) => handleTimesChange(e.target.value)}
                        />
                    </FormControl>
                    <Box
                        sx={{
                            pt: {xs: 0, sm: 4},
                            pl: {sm: 0},
                            pb: {xs: 1},
                            border: 0,
                            textAlign: {xs: 'left', sm: 'center'},
                        }}
                    >
                        <Typography
                            variant='h5'
                            component='div'
                            sx={{color: 'primary.main', minWidth: '4rem'}}
                        >
                            {t('or').toUpperCase()}
                        </Typography>
                    </Box>
                    <FormControl sx={{mb: 2}}>
                        <FormLabel sx={{mb: 1}}>
                            {t('ii_age_of_device')}:
                        </FormLabel>
                        <TextField
                            sx={{width: '11rem'}}
                            size='small'
                            error={!!errors.numberOfTimesUsed}
                            value={data.ageOfDevice || ''}
                            onChange={(e) => handleAgeChange(e.target.value)}
                        />
                    </FormControl>
                </Stack>
                <FormHelperText
                    sx={{pl: 2}}
                    error={!!errors.numberOfTimesUsed}
                >
                    {errors.numberOfTimesUsed}
                </FormHelperText>
            </Stack>

            <Stack
                direction='row-reverse'
                spacing={2}
                sx={{pt: 4, width: '100%'}}
            >
                <Button
                    variant='contained'
                    color='primary'
                    onClick={() => handleSubmit()}
                >
                    {t('continue')}
                </Button>

                <PreviousButton before={() => dispatch(setInstrumentInfo(data))} />

                <CancelButton/>
            </Stack>
        </Grid>
    )
}

export default InstrumentInformation
